import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { FileText, Upload, Link2, ExternalLink, Check, X, Send, UserCheck, Undo2, ClipboardList, Download, Trash2, ChevronDown, AlertCircle } from 'lucide-react';
import {
  getOffers, createOffer, transitionOffer, uploadOfferAttachmentPdf,
  getPositionBindings, getBindableSlots,
  getSalaryMaterials, uploadSalaryMaterials, deleteSalaryMaterial,
  getOfferApprovalPreview,
  OfferTemplateType,
} from '../api';
import { formatBeijingTime } from '../utils/dateFormat';
import {
  Offer, OfferAction, OfferStatus, OFFER_STATUS_LABELS,
  HeadcountSlot, HC_STATUS_LABELS, HC_EMPLOYMENT_LABELS, HcEmploymentType, User,
  OfferApprovalNode, OfferApprovalApprover,
} from '../types';
import OfferLetterModal from './OfferLetterModal';

interface Props {
  candidate: { id: string; name: string; position_id?: string | null; stage: string };
  currentUser: User;
  isHr: boolean;
  users: User[];
  onChanged: () => void;
}

/** 可选编制项：已绑定(招聘中)可直接发；未绑定(空闲)选中后自动绑定再发 */
interface SlotOption {
  slot: HeadcountSlot;
  bound: boolean;
}

const EMPLOYMENT_TYPES: HcEmploymentType[] = ['fulltime', 'intern', 'outsource', 'consultant', 'temp'];

/** 每个 Offer 状态下 HR 可执行的流转动作 */
const ACTIONS_BY_STATUS: Record<OfferStatus, { action: OfferAction; label: string; tone: string; icon: React.ReactNode }[]> = {
  pending_approval: [
    { action: 'approve', label: '审批通过', tone: 'green', icon: <Check size={13} /> },
    { action: 'reject', label: '审批拒绝', tone: 'red', icon: <X size={13} /> },
  ],
  approved: [
    { action: 'send', label: '发送候选人', tone: 'blue', icon: <Send size={13} /> },
  ],
  sent: [
    { action: 'accept', label: '候选人接受', tone: 'green', icon: <UserCheck size={13} /> },
    { action: 'decline', label: '候选人拒绝', tone: 'red', icon: <X size={13} /> },
  ],
  accepted: [
    { action: 'complete', label: '确认入职', tone: 'green', icon: <Check size={13} /> },
  ],
  rejected: [], declined: [], withdrawn: [], completed: [],
};

const TONE_CLASS: Record<string, string> = {
  green: 'text-white bg-primary-600 hover:bg-primary-700',
  red: 'text-gray-900 bg-white border border-gray-900 hover:bg-gray-900 hover:text-white',
  blue: 'text-white bg-primary-600 hover:bg-primary-700',
  gray: 'text-gray-600 bg-gray-100 hover:bg-gray-200',
};

const STATUS_BADGE: Record<OfferStatus, string> = {
  pending_approval: 'bg-gray-100 text-gray-600',
  approved: 'bg-primary-50 text-primary-600',
  rejected: 'bg-gray-900 text-white',
  sent: 'bg-primary-50 text-primary-600',
  accepted: 'bg-primary-50 text-primary-700',
  declined: 'bg-gray-100 text-gray-500',
  withdrawn: 'bg-gray-100 text-gray-500',
  completed: 'bg-primary-50 text-primary-700',
};

const NODE_STATUS_LABEL: Record<string, string> = {
  pending: '待审批', approved: '已通过', rejected: '已拒绝', skipped: '已跳过',
};
const NODE_STATUS_CLASS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  approved: 'bg-primary-50 text-primary-600',
  rejected: 'bg-gray-900 text-white',
  skipped: 'bg-gray-100 text-gray-400',
};

function approverBadge(a: OfferApprovalApprover): { text: string; cls: string } {
  if (a.status === 'approved') return { text: a.auto ? '知会' : '✓ 已通过', cls: 'text-primary-600' };
  if (a.status === 'rejected') return { text: '✗ 已拒绝', cls: 'text-gray-900' };
  if (a.status === 'skipped') return { text: '↷ 跳过', cls: 'text-gray-400' };
  return { text: '待审批', cls: 'text-gray-500' };
}

/** 将前端选择的覆盖审批人套用到预览链（仅用于发起表单展示） */
function applyOverrides(
  chain: OfferApprovalNode[],
  overrides: Record<string, { user_id: string; name?: string }>,
  users: User[],
): OfferApprovalNode[] {
  const mk = (userId: string, label: string): OfferApprovalApprover => {
    const u = users.find(x => x.id === userId);
    return { user_id: userId, name: u ? (u.display_name || u.username) : userId, role_label: label, status: 'pending' };
  };
  return chain.map(node => {
    if (node.key === 'ceo_cfo') {
      const approvers = [...node.approvers];
      (['ceo', 'cfo'] as const).forEach(rk => {
        const ov = overrides[rk];
        if (ov && ov.user_id) {
          const label = rk.toUpperCase();
          const entry = mk(ov.user_id, label);
          const idx = approvers.findIndex(a => a.role_label === label);
          if (idx >= 0) approvers[idx] = entry; else approvers.push(entry);
        }
      });
      return { ...node, approvers };
    }
    const ovKey = node.key === 'business_vp' ? 'business_vp' : 'hrd';
    const ov = overrides[ovKey];
    if (ov && ov.user_id) {
      return { ...node, approvers: [mk(ov.user_id, node.label)] };
    }
    return node;
  });
}

/** 节点式审批链横向步骤展示（居中、放大、已通过为蓝底白勾阴影图标） */
function ApprovalChainStepper({ chain, currentStage, offerStatus }: { chain: OfferApprovalNode[]; currentStage: number; offerStatus: OfferStatus }) {
  if (!Array.isArray(chain) || chain.length === 0) return null;
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-6">
      <div className="flex items-start justify-between">
        {chain.map((node, idx) => {
          const isCurrent = offerStatus === 'pending_approval' && currentStage === idx && node.status === 'pending';
          const isApproved = node.status === 'approved';
          const isRejected = node.status === 'rejected';
          const isSkipped = node.status === 'skipped';
          const isDone = isApproved || isRejected || isSkipped;
          const circleCls = isApproved
            ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
            : isRejected
              ? 'bg-gray-900 text-white shadow-lg shadow-gray-900/20'
              : isSkipped
                ? 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                : isCurrent
                  ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30 ring-4 ring-primary-100'
                  : 'bg-white text-gray-400 border-2 border-gray-200';
          return (
            <React.Fragment key={node.key}>
              <div className="flex flex-col items-center text-center flex-1 min-w-0 px-1">
                <div className={clsx('w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold transition-all', circleCls)}>
                  {isApproved ? <Check size={24} strokeWidth={3} />
                    : isRejected ? <X size={24} strokeWidth={3} />
                    : idx + 1}
                </div>
                <div className="mt-3 flex items-center justify-center flex-wrap gap-1.5">
                  <span className="text-sm font-semibold text-gray-900">{node.label}</span>
                  {node.mode === 'notify' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-500">知会</span>}
                  {node.sign === 'countersign' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-500">会签</span>}
                </div>
                <span className={clsx('mt-1.5 inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium',
                  isCurrent ? 'bg-primary-50 text-primary-600' : NODE_STATUS_CLASS[node.status] || NODE_STATUS_CLASS.pending)}>
                  {isCurrent ? '审批中' : (NODE_STATUS_LABEL[node.status] || node.status)}
                </span>
                <div className="mt-2.5 flex flex-col items-center gap-1">
                  {node.approvers.length === 0 ? (
                    <span className="text-[11px] text-gray-400">未配置 · 自动跳过</span>
                  ) : node.approvers.map(a => {
                    const b = approverBadge(a);
                    return (
                      <span key={a.user_id} className="text-[11px] text-gray-600 leading-tight">
                        {a.name}
                        {a.role_label ? <span className="text-gray-400"> · {a.role_label}</span> : null}
                        <span className={clsx('ml-1 font-medium', b.cls)}>{b.text}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
              {idx < chain.length - 1 && (
                <div className="flex-shrink-0 w-6 sm:w-12 pt-6">
                  <div className={clsx('h-0.5 w-full rounded-full', isDone ? 'bg-primary-300' : 'bg-gray-200')} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

const infoLabelCls = 'text-[11px] font-medium text-gray-400';
const infoValueCls = 'mt-0.5 text-sm font-medium text-gray-800';

export default function OfferDecisionPanel({ candidate, currentUser, isHr, users, onChanged }: Props) {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [slotOptions, setSlotOptions] = useState<SlotOption[]>([]);
  const [letterState, setLetterState] = useState<{ initial: Record<string, string>; type: OfferTemplateType } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // 发起 Offer 表单
  const [slotId, setSlotId] = useState('');
  const [salary, setSalary] = useState('');
  const [employmentType, setEmploymentType] = useState<HcEmploymentType>('fulltime');
  const [onboardDate, setOnboardDate] = useState('');
  const [probation, setProbation] = useState('3');
  const [workLocation, setWorkLocation] = useState('');
  const [reason, setReason] = useState('');
  const [approverIds, setApproverIds] = useState<string[]>([]);
  const [ccOpen, setCcOpen] = useState(false);
  const [offerImage, setOfferImage] = useState<File | null>(null);
  // 节点式审批链预览 + 覆盖（业务VP自动=部门负责人；HRD/CEO/CFO来自系统设置，可覆盖）
  const [chainPreview, setChainPreview] = useState<OfferApprovalNode[]>([]);
  const [approvalOverrides, setApprovalOverrides] = useState<Record<string, { user_id: string; name?: string }>>({});
  // 薪酬明细待填字段（来自 Offer 录用通知书）
  const [compAmount, setCompAmount] = useState('');
  const [compUnit, setCompUnit] = useState('月');
  const [compCurrency, setCompCurrency] = useState('RMB');
  const [compPretax, setCompPretax] = useState(true);
  const [internEndDate, setInternEndDate] = useState('');
  const [acceptDeadline, setAcceptDeadline] = useState('');
  const [perks, setPerks] = useState('');
  // 社招（正式）专有薪酬字段
  const [contractEndDate, setContractEndDate] = useState('');
  const [probationEndDate, setProbationEndDate] = useState('');
  const [probationSalary, setProbationSalary] = useState('');
  const [yearEndMultiplier, setYearEndMultiplier] = useState('');
  const [equityValue, setEquityValue] = useState('');
  // 薪资拆细（北森模式）
  const [fullAttendanceBonus, setFullAttendanceBonus] = useState('');
  const [postAllowance, setPostAllowance] = useState('');
  const [transportAllowance, setTransportAllowance] = useState('');
  const [skillAllowancePre, setSkillAllowancePre] = useState('');
  const [skillAllowancePost, setSkillAllowancePost] = useState('');
  const [managementAllowance, setManagementAllowance] = useState('');
  const [monthlyPerformance, setMonthlyPerformance] = useState('');
  const [quarterlyPerformance, setQuarterlyPerformance] = useState('');
  const [halfYearPerformance, setHalfYearPerformance] = useState('');
  const [signOnBonus, setSignOnBonus] = useState('');
  // 其他说明
  const [hasNoncompete, setHasNoncompete] = useState(false);
  const [otherNotes, setOtherNotes] = useState('');
  // 北森模式附件
  const [assessmentFile, setAssessmentFile] = useState<File | null>(null);
  const [backgroundCheckFile, setBackgroundCheckFile] = useState<File | null>(null);
  const [noncompeteFile, setNoncompeteFile] = useState<File | null>(null);
  // 全屏弹窗
  const [showModal, setShowModal] = useState(false);

  // 薪酬材料
  const [salMats, setSalMats] = useState<any[]>([]);
  const [salFiles, setSalFiles] = useState<File[]>([]);
  const [salNote, setSalNote] = useState('');
  const [salUploading, setSalUploading] = useState(false);

  // 审批/流转意见
  const [commentById, setCommentById] = useState<Record<string, string>>({});

  const positionId = candidate.position_id || '';

  const load = async () => {
    setLoading(true);
    try {
      const [ofs, mats] = await Promise.all([
        getOffers({ candidate_id: candidate.id }),
        getSalaryMaterials(candidate.id).catch(() => []),
      ]);
      setOffers(Array.isArray(ofs) ? ofs : []);
      setSalMats(Array.isArray(mats) ? mats : []);
      if (positionId) {
        const [bindings, bindable] = await Promise.all([
          getPositionBindings(positionId).catch(() => []),
          getBindableSlots(positionId).catch(() => []),
        ]);
        const opts: SlotOption[] = [];
        // 已绑定且处于招聘中的编制：可直接发 Offer
        (Array.isArray(bindings) ? bindings : [])
          .filter((b: any) => b.status === 'active' && b.slot && b.slot.status === 'RECRUITING')
          .forEach((b: any) => opts.push({ slot: b.slot, bound: true }));
        // 同部门空闲编制：选中后自动绑定再发
        (Array.isArray(bindable) ? bindable : []).forEach((s: HeadcountSlot) => opts.push({ slot: s, bound: false }));
        setSlotOptions(opts);
      } else {
        setSlotOptions([]);
      }
    } catch {
      toast.error('加载 Offer 数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidate.id, positionId]);

  // 选定编制后，拉取该编制将生成的审批链预览（业务VP由部门负责人自动解析）
  useEffect(() => {
    if (!showModal || !slotId) { setChainPreview([]); return; }
    let cancelled = false;
    getOfferApprovalPreview(slotId)
      .then(r => { if (!cancelled) setChainPreview(applyOverrides(r.approval_chain || [], approvalOverrides, users)); })
      .catch(() => { if (!cancelled) setChainPreview([]); });
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [showModal, slotId, approvalOverrides]);

  const activeOffer = useMemo(() => offers.find(o => o.in_progress), [offers]);
  /** 当前用户是否为该 Offer 当前活动节点待审批人（后端下发 pending_for_me） */
  const canActNow = (o: Offer) => o.pending_for_me === true || currentUser.role === 'admin';

  const handleCreate = async () => {
    if (!positionId) { toast.error('候选人未关联岗位，请先在候选人信息中设置岗位'); return; }
    if (!slotId) { toast.error('请选择要占用的编制'); return; }
    const isIntern = employmentType === 'intern';
    setBusy(true);
    try {
      await createOffer({
        candidate_id: candidate.id,
        headcount_slot_id: slotId,
        salary_annual: salary ? Number(salary) : undefined,
        employment_type: employmentType,
        expected_onboard_date: onboardDate || undefined,
        probation_months: probation ? Number(probation) : undefined,
        work_location: workLocation || undefined,
        comp_amount: compAmount ? Number(compAmount) : undefined,
        comp_unit: compUnit || undefined,
        comp_currency: compCurrency || undefined,
        comp_pretax: compPretax,
        intern_end_date: isIntern ? (internEndDate || undefined) : undefined,
        accept_deadline: acceptDeadline || undefined,
        perks: perks || undefined,
        contract_end_date: isIntern ? undefined : (contractEndDate || undefined),
        probation_end_date: isIntern ? undefined : (probationEndDate || undefined),
        probation_salary: isIntern ? undefined : (probationSalary ? Number(probationSalary) : undefined),
        year_end_multiplier: isIntern ? undefined : (yearEndMultiplier || undefined),
        equity_value: isIntern ? undefined : (equityValue ? Number(equityValue) : undefined),
        full_attendance_bonus: isIntern ? undefined : (fullAttendanceBonus ? Number(fullAttendanceBonus) : undefined),
        post_allowance: isIntern ? undefined : (postAllowance ? Number(postAllowance) : undefined),
        transport_allowance: isIntern ? undefined : (transportAllowance ? Number(transportAllowance) : undefined),
        skill_allowance_pre: isIntern ? undefined : (skillAllowancePre ? Number(skillAllowancePre) : undefined),
        skill_allowance_post: isIntern ? undefined : (skillAllowancePost ? Number(skillAllowancePost) : undefined),
        management_allowance: isIntern ? undefined : (managementAllowance ? Number(managementAllowance) : undefined),
        monthly_performance: isIntern ? undefined : (monthlyPerformance ? Number(monthlyPerformance) : undefined),
        quarterly_performance: isIntern ? undefined : (quarterlyPerformance ? Number(quarterlyPerformance) : undefined),
        half_year_performance: isIntern ? undefined : (halfYearPerformance ? Number(halfYearPerformance) : undefined),
        sign_on_bonus: isIntern ? undefined : (signOnBonus ? Number(signOnBonus) : undefined),
        has_noncompete: hasNoncompete,
        other_notes: otherNotes || undefined,
        reason: reason || undefined,
        approver_user_ids: approverIds,
        approval_overrides: Object.keys(approvalOverrides).length ? approvalOverrides : undefined,
        offer_image: offerImage,
        assessment_file: assessmentFile,
        background_check_file: backgroundCheckFile,
        noncompete_file: noncompeteFile,
      });
      toast.success('Offer 已发起，编制已锁定');
      setSlotId(''); setSalary(''); setOnboardDate(''); setWorkLocation('');
      setCompAmount(''); setInternEndDate(''); setAcceptDeadline(''); setPerks('');
      setContractEndDate(''); setProbationEndDate(''); setProbationSalary('');
      setYearEndMultiplier(''); setEquityValue('');
      setFullAttendanceBonus(''); setPostAllowance(''); setTransportAllowance('');
      setSkillAllowancePre(''); setSkillAllowancePost(''); setManagementAllowance('');
      setMonthlyPerformance(''); setQuarterlyPerformance(''); setHalfYearPerformance('');
      setSignOnBonus(''); setHasNoncompete(false); setOtherNotes('');
      setAssessmentFile(null); setBackgroundCheckFile(null); setNoncompeteFile(null);
      setReason(''); setApproverIds([]); setCcOpen(false); setOfferImage(null); setShowModal(false);
      setApprovalOverrides({}); setChainPreview([]);
      await load();
      onChanged();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '发起 Offer 失败');
    } finally {
      setBusy(false);
    }
  };

  const handleSalaryUpload = async () => {
    if (!salFiles.length) return;
    setSalUploading(true);
    try {
      await uploadSalaryMaterials(candidate.id, salFiles, salNote || undefined);
      toast.success('薪酬材料上传成功');
      setSalFiles([]); setSalNote('');
      setSalMats(await getSalaryMaterials(candidate.id));
    } catch { toast.error('上传失败'); }
    finally { setSalUploading(false); }
  };

  const handleSalaryDelete = async (id: string) => {
    if (!window.confirm('确认删除该薪酬材料？')) return;
    try { await deleteSalaryMaterial(id); toast.success('已删除'); setSalMats(await getSalaryMaterials(candidate.id)); }
    catch { toast.error('删除失败'); }
  };

  const openLetterFor = (o: Offer) => {
    const amount = o.comp_amount || 0;
    const basic = amount ? String(amount) : (o.salary_annual ? String(o.salary_annual) : '');
    // 试用期月薪：优先取录入值，否则按转正月薪 80% 估算
    const probation = o.probation_salary ? String(o.probation_salary) : (amount ? String(Math.round(amount * 0.8)) : '');
    const isIntern = o.employment_type === 'intern';
    const hasEquity = (o.equity_value || 0) > 0;
    setLetterState({
      type: isIntern ? 'intern' : (hasEquity ? 'formal_equity' : 'formal_no_equity'),
      initial: {
        // 通用
        candidate_name_cn: candidate.name || '',
        work_location_cn: o.work_location || '深圳',
        work_location_en: /深圳/.test(o.work_location || '') ? 'Shenzhen' : '',
        start_date: o.expected_onboard_date || '',
        accept_deadline: o.accept_deadline || '',
        sign_date: new Date().toISOString().slice(0, 10),
        // 实习模版
        intern_end_date: o.intern_end_date || '',
        allowance_amount: basic,
        allowance_currency: o.comp_currency || 'RMB',
        allowance_unit: o.comp_unit || '',
        // 社招模版
        basic_salary: basic,
        probation_salary: probation,
        contract_end_date: o.contract_end_date || '',
        probation_end_date: o.probation_end_date || '',
        year_end_multiplier: o.year_end_multiplier || '',
        equity_value: o.equity_value ? String(o.equity_value) : '',
      },
    });
  };

  const handleTransition = async (offer: Offer, action: OfferAction) => {
    const needReason = action === 'reject' || action === 'decline' || action === 'withdraw';
    const reasonText = commentById[offer.id] || '';
    if (needReason && !reasonText.trim() && action !== 'withdraw') {
      toast.error('请填写原因');
      return;
    }
    setBusy(true);
    try {
      await transitionOffer(offer.id, action, reasonText);
      toast.success(`${OFFER_STATUS_LABELS[offer.status]} → 已${action === 'complete' ? '确认入职' : '处理'}`);
      setCommentById(prev => ({ ...prev, [offer.id]: '' }));
      await load();
      onChanged();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '操作失败');
    } finally {
      setBusy(false);
    }
  };

  const handleUploadPdf = async (offer: Offer, file: File) => {
    setBusy(true);
    try {
      await uploadOfferAttachmentPdf(offer.id, file);
      toast.success('Offer PDF 已上传');
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '上传失败');
    } finally {
      setBusy(false);
    }
  };

  const toggleApprover = (id: string) =>
    setApproverIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // 可发起 Offer：HR + 已关联岗位 + 无进行中 Offer
  const canCreate = isHr && !!positionId && !activeOffer;
  const isIntern = employmentType === 'intern';

  // 表单通用样式
  const inputCls = 'w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500';
  const labelCls = 'block text-xs font-medium text-gray-500 mb-1';
  const req = <span className="text-gray-500"> *</span>;
  const secTitle = (t: string) => (
    <div className="flex items-center gap-2">
      <span className="w-1 h-4 rounded-full bg-primary-500 inline-block" />
      <h4 className="text-sm font-semibold text-gray-800">{t}</h4>
    </div>
  );
  const fileField = (label: string, file: File | null, setFile: (f: File | null) => void, accept: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input type="file" accept={accept} onChange={e => setFile(e.target.files?.[0] || null)}
        className="w-full text-xs text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary-50 file:text-primary-600 hover:file:bg-primary-100" />
      {file && <span className="text-[11px] text-gray-500 mt-0.5 inline-block">已选：{file.name}</span>}
    </div>
  );

  // 审批节点下拉（业务VP/HRD/CEO/CFO）——北森风格：请选择（留空=自动解析）
  const overrideSelect = (label: string, key: 'business_vp' | 'hrd' | 'ceo' | 'cfo', hint?: string) => {
    const auto = chainPreview.find(n =>
      (key === 'business_vp' && n.key === 'business_vp') ||
      (key === 'hrd' && n.key === 'hrd') ||
      ((key === 'ceo' || key === 'cfo') && n.key === 'ceo_cfo'));
    const autoName = key === 'cfo' ? auto?.approvers?.[1]?.name : auto?.approvers?.[0]?.name;
    return (
      <div>
        <label className={labelCls}>{label}{hint && <span className="text-gray-400 font-normal"> · {hint}</span>}</label>
        <div className="relative">
          <select
            value={approvalOverrides[key]?.user_id || ''}
            onChange={e => {
              const v = e.target.value;
              setApprovalOverrides(prev => {
                const next = { ...prev };
                if (!v) delete next[key];
                else next[key] = { user_id: v };
                return next;
              });
            }}
            className={clsx(inputCls, 'appearance-none pr-8', !approvalOverrides[key]?.user_id && 'text-gray-400')}>
            <option value="">{autoName ? `自动：${autoName}` : '请选择'}</option>
            {users.map(u => (
              <option key={u.id} value={u.id} className="text-gray-800">{u.display_name || u.username}</option>
            ))}
          </select>
          <ChevronDown size={15} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
      </div>
    );
  };

  const salarySection = (
    <div className="rounded-xl border border-primary-100 bg-primary-50/40 p-3 space-y-2.5">
      <div className="text-xs font-semibold text-primary-700 flex items-center gap-1.5">
        <FileText size={13} />薪酬材料
        {salMats.length > 0 && <span className="text-gray-400 font-normal">({salMats.length})</span>}
      </div>
      {isHr && (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
            onChange={e => setSalFiles(Array.from(e.target.files || []))}
            className="flex-1 min-w-[180px] text-xs text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200"
          />
          <input value={salNote} onChange={e => setSalNote(e.target.value)} placeholder="备注（可选）"
            className="h-8 rounded-lg border border-gray-200 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500" />
          <button
            disabled={salFiles.length === 0 || salUploading}
            onClick={handleSalaryUpload}
            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 rounded-lg transition-colors shrink-0">
            {salUploading ? '上传中...' : '上传'}
          </button>
          {salFiles.length > 0 && <span className="text-[11px] text-gray-500 w-full">已选 {salFiles.length} 个文件</span>}
        </div>
      )}
      {salMats.length === 0 ? (
        <div className="text-[11px] text-gray-400">暂无薪酬材料</div>
      ) : (
        <div className="space-y-1.5">
          {salMats.map((m: any) => {
            const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(m.file_name);
            const isPdf = /\.pdf$/i.test(m.file_name);
            const isExcel = /\.(xls|xlsx)$/i.test(m.file_name);
            return (
              <div key={m.id} className="bg-white rounded-lg border border-gray-200 p-2 flex items-center gap-2.5">
                <span className={clsx('shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[9px] font-bold',
                  isPdf ? 'bg-gray-50 text-gray-500' : isExcel ? 'bg-primary-50 text-primary-600' : isImg ? 'bg-primary-50 text-primary-500' : 'bg-gray-50 text-gray-500')}>
                  {isPdf ? 'PDF' : isExcel ? 'XLS' : isImg ? 'IMG' : 'FILE'}
                </span>
                <div className="flex-1 min-w-0">
                  <a href={m.file_url} target="_blank" rel="noreferrer" className="text-xs font-medium text-gray-800 hover:text-primary-600 truncate block">{m.file_name}</a>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-400">
                    <span>{m.uploader_name || '未知'}</span>
                    <span>{formatBeijingTime(m.created_at, 'date')}</span>
                    {m.note && <span>· {m.note}</span>}
                  </div>
                </div>
                <a href={m.file_url} target="_blank" rel="noreferrer" className="p-1 text-gray-400 hover:text-primary-600" title="下载"><Download size={13} /></a>
                {isHr && (
                  <button onClick={() => handleSalaryDelete(m.id)} className="p-1 text-gray-300 hover:text-gray-500" title="删除"><Trash2 size={13} /></button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (loading) {
    return <p className="text-center text-xs text-gray-400 py-6">加载中…</p>;
  }

  return (
    <div className="space-y-4">
      {/* 头部 + 发起按钮 */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
          <span className="w-1.5 h-4 rounded-full bg-primary-500 inline-block" />
          Offer 与编制占用
          {offers.length > 0 && <span className="text-xs text-gray-400 font-normal">({offers.length})</span>}
        </h3>
        {canCreate && (
          <button onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
            <FileText size={13} />发起 Offer
          </button>
        )}
      </div>

      {/* 无岗位提示 */}
      {!positionId && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-700">
          候选人尚未关联岗位，无法发起 Offer。请先在候选人信息中设置意向岗位。
        </div>
      )}

      {/* 薪酬材料（面板内常驻） */}
      {salarySection}

      {/* 发起 Offer 全屏弹窗（北森分节模式） */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex justify-center bg-black/40" onClick={() => !busy && setShowModal(false)}>
          <div className="bg-gray-50 w-full max-w-3xl h-full shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            {/* 弹窗头部 */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-white shrink-0">
              <h3 className="text-base font-semibold text-gray-800">发起 Offer · {candidate.name}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><X size={18} /></button>
            </div>
            {/* 内容区（滚动） */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">

              {/* ===== 需求信息 ===== */}
              <section className="space-y-3">
                {secTitle('需求信息')}

          {/* 编制选择 */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">占用编制 <span className="text-gray-500">*</span></label>
            {slotOptions.length === 0 ? (
              <div className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
                该岗位所在部门暂无可用编制。请在<span className="font-medium text-gray-600">岗位详情 → 编制绑定</span>中确认，或联系管理员新增编制。
              </div>
            ) : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {slotOptions.map(({ slot, bound }) => (
                  <label key={slot.id}
                    className={clsx('flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs',
                      slotId === slot.id ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:bg-gray-50')}>
                    <input type="radio" name="slot" checked={slotId === slot.id}
                      onChange={() => setSlotId(slot.id)} className="accent-primary-600" />
                    <span className="font-mono font-medium text-gray-700">{slot.code}</span>
                    {slot.position_name && <span className="font-medium text-gray-800">{slot.position_name}</span>}
                    <span className="text-gray-500">{slot.job_level}</span>
                    <span className="text-gray-400">{HC_EMPLOYMENT_LABELS[slot.employment_type || 'fulltime']}</span>
                    <span className={clsx('ml-auto px-1.5 py-0.5 rounded text-[10px]',
                      bound ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-500')}>
                      {bound ? HC_STATUS_LABELS[slot.status] : '空闲·选中自动绑定'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
              </section>

              {/* ===== 基本信息 ===== */}
              <section className="space-y-3">
                {secTitle('基本信息')}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">候选人</label>
              <input readOnly value={candidate.name || ''}
                className="w-full h-9 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-600" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">年薪 (元)</label>
              <input type="number" value={salary} onChange={e => setSalary(e.target.value)}
                placeholder="如 420000"
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">用工类型</label>
              <select value={employmentType} onChange={e => setEmploymentType(e.target.value as HcEmploymentType)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{HC_EMPLOYMENT_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">预计入职日期</label>
              <input type="date" value={onboardDate} onChange={e => setOnboardDate(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">试用期 (月)</label>
              <input type="number" value={probation} onChange={e => setProbation(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">工作地点</label>
            <input value={workLocation} onChange={e => setWorkLocation(e.target.value)}
              className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
              </section>

              {/* ===== 薪资信息 ===== */}
              <section className="space-y-3">
                {secTitle('薪资信息')}

          {/* 薪酬明细（随用工类型动态切换；会带入生成的 Offer 附件） */}
          <div className="rounded-xl border border-primary-100 bg-primary-50/40 p-3 space-y-3">
            <div className="text-xs font-semibold text-primary-700">
              薪酬明细（用于生成 Offer 附件）
              <span className="ml-1.5 font-normal text-gray-400">· {employmentType === 'intern' ? '实习' : '社招 / 正式'}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {employmentType === 'intern' ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">薪酬 / 津贴金额</label>
                    <input type="number" value={compAmount} onChange={e => setCompAmount(e.target.value)}
                      placeholder="如 270"
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">计薪单位</label>
                    <select value={compUnit} onChange={e => setCompUnit(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                      <option value="天/Day">天 / Day</option>
                      <option value="月">月 / Month</option>
                      <option value="年">年 / Year</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">实习期结束日期</label>
                    <input type="date" value={internEndDate} onChange={e => setInternEndDate(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">转正月薪 (元)</label>
                    <input type="number" value={compAmount} onChange={e => setCompAmount(e.target.value)}
                      placeholder="如 30000"
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">试用期月薪 (元)</label>
                    <input type="number" value={probationSalary} onChange={e => setProbationSalary(e.target.value)}
                      placeholder="留空按转正 80% 估算"
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">试用期到期日</label>
                    <input type="date" value={probationEndDate} onChange={e => setProbationEndDate(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">劳动合同到期日</label>
                    <input type="date" value={contractEndDate} onChange={e => setContractEndDate(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">年终奖系数</label>
                    <input value={yearEndMultiplier} onChange={e => setYearEndMultiplier(e.target.value)}
                      placeholder="如 2"
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">期权参考总价值 (元，可选)</label>
                    <input type="number" value={equityValue} onChange={e => setEquityValue(e.target.value)}
                      placeholder="填写则用「社招·有期权」模版"
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">全勤奖 (元)</label>
                    <input type="number" value={fullAttendanceBonus} onChange={e => setFullAttendanceBonus(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">岗位津贴 (元)</label>
                    <input type="number" value={postAllowance} onChange={e => setPostAllowance(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">交通补贴 (元)</label>
                    <input type="number" value={transportAllowance} onChange={e => setTransportAllowance(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">转正前技能津贴 (元)</label>
                    <input type="number" value={skillAllowancePre} onChange={e => setSkillAllowancePre(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">转正后技能津贴 (元)</label>
                    <input type="number" value={skillAllowancePost} onChange={e => setSkillAllowancePost(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">管理津贴 (元)</label>
                    <input type="number" value={managementAllowance} onChange={e => setManagementAllowance(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">月度绩效工资 (元)</label>
                    <input type="number" value={monthlyPerformance} onChange={e => setMonthlyPerformance(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">季度绩效工资 (元)</label>
                    <input type="number" value={quarterlyPerformance} onChange={e => setQuarterlyPerformance(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">半年绩效工资 (元)</label>
                    <input type="number" value={halfYearPerformance} onChange={e => setHalfYearPerformance(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">签字费 (元)</label>
                    <input type="number" value={signOnBonus} onChange={e => setSignOnBonus(e.target.value)}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">币种</label>
                <input value={compCurrency} onChange={e => setCompCurrency(e.target.value)}
                  placeholder="RMB"
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">计税方式</label>
                <select value={compPretax ? '1' : '0'} onChange={e => setCompPretax(e.target.value === '1')}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                  <option value="1">税前</option>
                  <option value="0">税后</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">接受录用截止日期</label>
                <input type="date" value={acceptDeadline} onChange={e => setAcceptDeadline(e.target.value)}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">福利说明</label>
              <input value={perks} onChange={e => setPerks(e.target.value)}
                placeholder="如：餐补、下午茶、节假日礼品"
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
          </div>

              {/* 定薪方案说明：薪酬材料 */}
              {salarySection}
              </section>

              {/* ===== 其他说明 ===== */}
              <section className="space-y-3">
                {secTitle('其他说明')}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>是否存在正在执行的竞业协议</label>
                    <div className="flex items-center gap-4 h-9">
                      <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                        <input type="radio" name="noncompete" checked={hasNoncompete} onChange={() => setHasNoncompete(true)} className="accent-primary-600" />是
                      </label>
                      <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                        <input type="radio" name="noncompete" checked={!hasNoncompete} onChange={() => setHasNoncompete(false)} className="accent-primary-600" />否
                      </label>
                    </div>
                  </div>
                  {hasNoncompete && fileField('竞业协议附件', noncompeteFile, setNoncompeteFile, '.pdf,.doc,.docx,.jpg,.jpeg,.png')}
                  {fileField('测评附件', assessmentFile, setAssessmentFile, '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png')}
                  {fileField('背调附件', backgroundCheckFile, setBackgroundCheckFile, '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png')}
                </div>
                <div>
                  <label className={labelCls}>发起理由 / 薪酬说明</label>
                  <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
                    placeholder="薪酬构成、职级依据等说明"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                </div>
                <div>
                  <label className={labelCls}>其他说明</label>
                  <textarea value={otherNotes} onChange={e => setOtherNotes(e.target.value)} rows={2}
                    placeholder="其他补充说明"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                </div>
              </section>

              {/* ===== 审批流程 ===== */}
              <section className="space-y-3">
                {secTitle('审批流程（业务VP → HRD → CEO+CFO）')}

                {/* 审批节点下拉（默认自动解析，可指定） */}
                <div className="grid grid-cols-2 gap-3">
                  {overrideSelect('业务VP', 'business_vp', '仅知会')}
                  {overrideSelect('HRD', 'hrd', '硬审批')}
                  {overrideSelect('CEO', 'ceo', '会签')}
                  {overrideSelect('CFO', 'cfo', '会签')}
                </div>

                {/* 审批链预览 */}
                {chainPreview.length > 0 && (
                  <ApprovalChainStepper chain={chainPreview} currentStage={-1} offerStatus={'pending_approval' as OfferStatus} />
                )}
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  留空则自动解析（业务VP=编制所属部门负责人，HRD/CEO/CFO=系统设置）。业务VP 仅知会不阻断（无薪酬）；CEO+CFO 会签，仅可见 Offer 附件；相邻节点同一人自动跳过。
                </p>

                {/* 抄送 / 额外知会人：北森风格下拉多选 */}
                <div>
                  <label className={labelCls}>抄送（可选，将收到飞书通知）</label>
                  <div className="relative">
                    <button type="button" onClick={() => setCcOpen(o => !o)}
                      className={clsx('w-full min-h-9 rounded-lg border border-gray-200 pl-3 pr-8 py-1.5 text-left text-sm focus:outline-none focus:ring-1 focus:ring-primary-500',
                        ccOpen && 'ring-1 ring-primary-500')}>
                      {approverIds.length === 0 ? (
                        <span className="text-gray-400">请选择</span>
                      ) : (
                        <span className="flex flex-wrap gap-1">
                          {approverIds.map(id => {
                            const u = users.find(x => x.id === id);
                            return (
                              <span key={id} className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 rounded px-1.5 py-0.5 text-xs">
                                {u?.display_name || u?.username || id}
                                <X size={11} className="cursor-pointer hover:text-primary-900"
                                  onClick={e => { e.stopPropagation(); toggleApprover(id); }} />
                              </span>
                            );
                          })}
                        </span>
                      )}
                      <ChevronDown size={15} className={clsx('pointer-events-none absolute right-2.5 top-2.5 text-gray-400 transition-transform', ccOpen && 'rotate-180')} />
                    </button>
                    {ccOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setCcOpen(false)} />
                        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1">
                          {users.map(u => {
                            const checked = approverIds.includes(u.id);
                            return (
                              <button key={u.id} type="button" onClick={() => toggleApprover(u.id)}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-gray-50">
                                <span className={clsx('w-4 h-4 rounded border flex items-center justify-center shrink-0',
                                  checked ? 'bg-primary-500 border-primary-500' : 'border-gray-300')}>
                                  {checked && <Check size={12} className="text-white" />}
                                </span>
                                <span className="text-gray-700">{u.display_name || u.username}</span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {fileField('方案截图（可选）', offerImage, setOfferImage, 'image/*')}
              </section>

            </div>

            {/* 底部固定操作栏 */}
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-white shrink-0">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors">取消</button>
              <button onClick={handleCreate} disabled={busy || !slotId}
                className="px-5 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 rounded-lg transition-colors">
                {busy ? '提交中…' : '发起 Offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offer 列表 */}
      {offers.length === 0 ? (
        <p className="text-center text-xs text-gray-400 py-6">暂无 Offer 记录</p>
      ) : (
        <div className="space-y-2.5">
          {offers.map(o => {
            // 节点式审批：approve/reject 只对「当前节点待审批人」开放；其余流转仍归 HR
            const flowActions = (ACTIONS_BY_STATUS[o.status] || []).filter(a => a.action !== 'approve' && a.action !== 'reject');
            const showApproveReject = o.status === 'pending_approval' && canActNow(o);
            const showActionBar = showApproveReject || (isHr && o.in_progress);
            return (
              <div key={o.id} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                {/* 头部：编号 / 状态 / 编制 / 发起人 */}
                <div className="flex items-start justify-between gap-3 mb-3.5">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="font-mono text-sm font-semibold text-gray-800">{o.code}</span>
                    <span className={clsx('px-2.5 py-1 rounded-md text-xs font-medium', STATUS_BADGE[o.status])}>
                      {o.status_label || OFFER_STATUS_LABELS[o.status]}
                    </span>
                    {o.slot_code && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <Link2 size={12} />{o.slot_code}
                        <span className="text-gray-400">（{o.slot_status_label}）</span>
                      </span>
                    )}
                  </div>
                  {o.created_by_name && <span className="shrink-0 text-xs text-gray-400">发起人 · {o.created_by_name}</span>}
                </div>

                {/* 待附件提示 */}
                {o.awaiting_attachment && (
                  <div className="mb-4 flex items-start gap-2 rounded-xl border border-gray-900/10 bg-gray-900/[0.03] px-3.5 py-3 text-xs">
                    <AlertCircle size={15} className="mt-0.5 shrink-0 text-gray-900" />
                    <div className="text-gray-700 leading-relaxed">
                      <span className="font-semibold text-gray-900">待上传 Offer 附件</span>
                      <span className="ml-1">{isHr
                        ? '请先生成并上传 Offer 附件，CEO+CFO 会签需先看到附件后方可进行。'
                        : '已流转至 CEO+CFO 会签，待 HR 上传 Offer 附件后即可开始会签。'}</span>
                    </div>
                  </div>
                )}

                {/* 基本信息 */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-3.5 mb-4">
                  {o.salary_annual ? (
                    <div><div className={infoLabelCls}>年薪</div><div className={infoValueCls}>{o.salary_annual.toLocaleString()} 元</div></div>
                  ) : null}
                  {o.employment_type ? (
                    <div><div className={infoLabelCls}>用工类型</div><div className={infoValueCls}>{HC_EMPLOYMENT_LABELS[o.employment_type]}</div></div>
                  ) : null}
                  {o.expected_onboard_date ? (
                    <div><div className={infoLabelCls}>预计入职</div><div className={infoValueCls}>{o.expected_onboard_date}</div></div>
                  ) : null}
                  {o.work_location ? (
                    <div><div className={infoLabelCls}>工作地点</div><div className={infoValueCls}>{o.work_location}</div></div>
                  ) : null}
                </div>

                {o.reason && (
                  <div className="mb-4">
                    <div className={infoLabelCls}>发起理由 / 说明</div>
                    <p className="mt-1 text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-3">{o.reason}</p>
                  </div>
                )}

                {/* 节点式审批链 */}
                {Array.isArray(o.approval_chain) && o.approval_chain.length > 0 && (
                  <div className="mb-4">
                    <div className={clsx(infoLabelCls, 'mb-1.5')}>审批流程</div>
                    <ApprovalChainStepper chain={o.approval_chain} currentStage={o.approval_stage ?? -1} offerStatus={o.status} />
                  </div>
                )}
                {o.redacted === 'no_salary' && (
                  <p className="text-[11px] text-gray-400 mb-3">按审批节点可见范围，薪酬信息已对您隐藏。</p>
                )}
                {o.redacted === 'attachment_only' && (
                  <p className="text-[11px] text-gray-400 mb-3">按审批节点可见范围，您可查看 Offer 附件与基本信息。</p>
                )}

                {/* 审批意见展示 */}
                {o.approver_comment && (
                  <p className="text-xs text-gray-600 mb-4 bg-gray-50 rounded-lg px-3 py-2.5">
                    <span className="font-medium text-gray-800">{o.approver_name}</span>：{o.approver_comment}
                  </p>
                )}

                {/* Offer 附件面板 */}
                <div className="mb-1 rounded-xl border border-gray-200 bg-gray-50/70 p-4">
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                      <FileText size={15} className="text-primary-600" />Offer 附件
                    </div>
                    {isHr && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => openLetterFor(o)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors">
                          <FileText size={13} />生成录用通知书
                        </button>
                        <label className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg cursor-pointer transition-colors">
                          <Upload size={13} />{o.offer_pdf ? '重传附件' : '上传附件'}
                          <input type="file" accept="application/pdf" className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadPdf(o, f); }} />
                        </label>
                      </div>
                    )}
                  </div>
                  {o.offer_pdf ? (
                    <div className="flex flex-wrap items-center gap-2.5">
                      <a href={o.offer_pdf} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors">
                        <FileText size={15} />查看 Offer 附件（PDF）<ExternalLink size={13} className="text-gray-400" />
                      </a>
                      {o.offer_image && (
                        <a href={o.offer_image} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                          <ExternalLink size={15} />方案截图
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 text-xs text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg px-3.5 py-3.5">
                      <FileText size={15} className="text-gray-300 shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{isHr
                        ? '尚未上传 Offer 附件。CEO+CFO 会签前必须先生成并上传 Offer 附件。'
                        : '待 HR 上传 Offer 附件后，可在此查看录用通知书。'}
                        {o.offer_image && (
                          <> · <a href={o.offer_image} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">查看方案截图</a></>
                        )}
                      </span>
                    </div>
                  )}
                </div>

                {showActionBar && (
                  <div className="space-y-2 pt-2 border-t border-gray-100">
                    {/* 意见输入（审批 / 拒绝 / 撤回等） */}
                    <input value={commentById[o.id] || ''}
                      onChange={e => setCommentById(prev => ({ ...prev, [o.id]: e.target.value }))}
                      placeholder="审批 / 处理意见（拒绝、撤回时建议填写）"
                      className="w-full h-8 rounded-lg border border-gray-200 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500" />

                    <div className="flex flex-wrap items-center gap-2">
                      {/* 节点式审批：当前节点待审批人可 通过 / 拒绝 */}
                      {showApproveReject && (
                        <>
                          <button disabled={busy} onClick={() => handleTransition(o, 'approve')}
                            className={clsx('inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50', TONE_CLASS.green)}>
                            <Check size={13} />审批通过
                          </button>
                          <button disabled={busy} onClick={() => handleTransition(o, 'reject')}
                            className={clsx('inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50', TONE_CLASS.red)}>
                            <X size={13} />审批拒绝
                          </button>
                        </>
                      )}

                      {/* 其余流转仅 HR */}
                      {isHr && flowActions.map(a => (
                        <button key={a.action} disabled={busy}
                          onClick={() => handleTransition(o, a.action)}
                          className={clsx('inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50', TONE_CLASS[a.tone])}>
                          {a.icon}{a.label}
                        </button>
                      ))}

                      {/* 撤回：任何进行中状态（HR） */}
                      {isHr && o.in_progress && (
                        <button disabled={busy} onClick={() => handleTransition(o, 'withdraw')}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-600 rounded-lg transition-colors disabled:opacity-50">
                          <Undo2 size={13} />撤回
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="flex items-center gap-1 text-[11px] text-gray-400">
        <ClipboardList size={11} />
        Offer 通过后编制自动锁定；确认入职后编制转为「已占用」，候选人流转至「已入职」。
      </p>

      {letterState && (
        <OfferLetterModal initial={letterState.initial} defaultType={letterState.type} onClose={() => setLetterState(null)} />
      )}
    </div>
  );
}
